import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$ } from '../constants';
import { map, startWith, Subject, switchMap, take } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatDivider } from '@angular/material/divider';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { GalaxyAiIconService, GalaxyAiIconSvg } from '@vendasta/galaxy/ai-icon';
import { GalaxyTooltip } from '@vendasta/galaxy/tooltip';
import { AtlasMenuService } from '@galaxy/atlas';
import { DomSanitizer } from '@angular/platform-browser';
import { scheduleBot$ } from '@vendasta/protos/meeting_analysis/v1/meeting-bot-service';
import { Validator } from '@vendasta/shared';

@Component({
  selector: 'meeting-analysis-schedule-bot',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    MatInput,
    TranslateModule,
    MatButtonModule,
    MatDivider,
    MatIcon,
    GalaxyTooltip,
  ],
  templateUrl: './schedule-bot.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './schedule-bot.component.scss',
})
export class ScheduleBotComponent {
  formControl = new FormControl<string>('');

  invalidUrl$ = this.formControl.valueChanges.pipe(
    map((value) => {
      const result = Validator.isUrl(new FormControl(value));
      return result?.invalidUrl;
    }),
  );

  isUrlEmpty$ = this.formControl.valueChanges.pipe(
    map((value) => value === null || value === undefined || value.trim() === ''),
    startWith(true),
  );

  private readonly namespace$ = inject(MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$);
  private readonly alertService = inject(SnackbarService);
  private readonly _ = inject(GalaxyAiIconService);
  private readonly menuService = inject(AtlasMenuService);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);

  constructor() {
    const uniqueId = `ai_meeting_analysis_gradient_${Date.now()}`;
    const modifiedSvg = GalaxyAiIconSvg.replace('id="glxy_ai_svg_gradient"', `id="${uniqueId}"`).replace(
      'url(#glxy_ai_svg_gradient)',
      `url(#${uniqueId})`,
    );
    this.iconRegistry.addSvgIconLiteral(
      'ai-meeting-analysis-icon',
      this.sanitizer.bypassSecurityTrustHtml(modifiedSvg),
    );
  }

  joinMeeting(): void {
    const url = (this.formControl.value || '').trim();
    if (url) {
      this.namespace$
        .pipe(
          take(1),
          switchMap((namespace) =>
            scheduleBot$({
              namespace: namespace,
              meetingUrl: url,
            }),
          ),
        )
        .subscribe({
          next: () => {
            this.formControl.setValue('');
            this.close();
            this.alertService.openSuccessSnack('SCHEDULE_BOT.SUCCESS_V2');
          },
          error: (err) => {
            console.error(err);
            const errorKey = err?.status === 429 ? 'SCHEDULE_BOT.RATE_LIMITED' : 'SCHEDULE_BOT.ERROR';
            this.alertService.openErrorSnack(errorKey);
          },
        });
    }
  }

  private readonly close$$ = new Subject<void>();
  public get close$() {
    return this.close$$.asObservable();
  }
  close(): void {
    this.menuService.closeMenu('');
    this.close$$.next();
  }
}
